import React, {
  FunctionComponent,
  useState,
  useEffect,
  useContext,
  SyntheticEvent,
} from "react";
import { Button, ChoiceList } from "@cmsgov/ds-medicare-gov";
import {
  useTranslate,
  getSLSCode,
  getBeneInfoForCSR,
  getLowIncomeSubsidyStatusLabel,
  formatDate,
  fetchCurrentPlan,
  sendTealiumEvent,
  isOutsideOpenEnrollment,
  sendTealiumLink,
} from "../../helpers";
import {
  AppPage,
  CountyPicker,
  DegradedIntegrationAlert,
} from "../../components";
import InfoItem from "./InfoItem";
import { Routes } from "../../app";
import { AppContext } from "../../app/store";
import {
  County,
  ActionType,
  PlanType,
  PharmacyType,
  MctToastType,
  LowIncomeSubsidyStatus,
  FullPlanId,
} from "../../@types";
import { RouteComponentProps } from "react-router";
import { getBeneDrugs, getBeneOecStatus } from "../../api";
import {
  logError,
  makePlanDisplayId,
  getBeneFirstAndLastName,
} from "../../helpers";
import { useFlags } from "launchdarkly-react-client-sdk";
import { FormattedMessage } from "react-intl-phraseapp";
import { TealiumConversions } from "../../helpers/CONSTANTS";
import { fetchPlanIdForNextYear } from "../../helpers/planHelpers";
import URI from "urijs";
import { parseDegradedIntegrations } from "../../helpers/loginHelpers";

const CSRLandingPage: FunctionComponent<RouteComponentProps> = ({
  history,
  location,
}) => {
  const {
    dispatch,
    state,
    state: { beneficiary, currentCoverage, year, language, tealiumLoaded },
  } = useContext(AppContext);
  const {
    mctCurrentPlanYear,
    mctYpPublicPreview,
    mctYpOpenEnrollment,
    mctYpOeNextYear,
    mctYpOutsideOpenEnrollment,
  } = useFlags();
  const [canContinue, setCanContinue] = useState(false);
  const [isError, setIsError] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(true);
  const [beneFetched, setBeneFetched] = useState(false);
  const [lisHelpDifferent, setLisHelpDifferent] = useState(false);
  const [lisStatus, setLisStatus] = useState<
    LowIncomeSubsidyStatus | undefined
  >(undefined);
  const t = useTranslate();

  const outsideOpenEnrollment = isOutsideOpenEnrollment({
    mctYpPublicPreview,
    mctYpOpenEnrollment,
    mctYpOeNextYear,
    mctYpOutsideOpenEnrollment,
  });

  const getBeneInfo = async (): Promise<void> => {
    // Have to use the prop location here due to react-router messing with window.location.search
    const code = getSLSCode(location.search);

    if (code) {
      // Clear out any previous bene info, if not already cleared via sessionStorage
      dispatch({ type: ActionType.RESET_STATE });

      try {
        const { headers, beneficiary: beneInfo } = await getBeneInfoForCSR(
          code
        );

        const degradedIntegrations = parseDegradedIntegrations(headers);

        if (degradedIntegrations.length) {
          dispatch({
            type: ActionType.UPDATE_DEGRADED_INTEGRATIONS,
            payload: degradedIntegrations,
          });
        }

        dispatch({ type: ActionType.ADD_BENEFICIARY, payload: beneInfo });
        setBeneFetched(true);

        try {
          const drugs = await getBeneDrugs();

          dispatch({ type: ActionType.SET_PRESCRIPTIONS, payload: drugs });
        } catch (e) {
          logError("Failed to get beneficiary drugs (CSRLandingPage)", e);
        }

        if (beneInfo) {
          dispatch({
            type: ActionType.ADD_CSR,
            payload: { id: beneInfo.csr_id, activityId: beneInfo.activity_id },
          });

          dispatch({
            type: ActionType.UPDATE_LIS,
            payload: beneInfo.lis_level,
          });
          dispatch({
            type: ActionType.UPDATE_FUTURE_LIS,
            payload: beneInfo?.future_lis_level,
          });

          if (beneInfo.current_coverage.length) {
            let nextYearPlanId: FullPlanId | undefined;
            if (!outsideOpenEnrollment || mctYpOeNextYear) {
              nextYearPlanId = await fetchPlanIdForNextYear(
                beneInfo.current_coverage[0],
                "CSRLandingPage"
              );
            }
            fetchCurrentPlan(
              beneInfo,
              state.pharmacies,
              beneInfo.lis_level,
              dispatch,
              nextYearPlanId,
              beneInfo.future_lis_level
            ).finally(() => {
              setLoadingPlan(false);
            });
          }
        }
      } catch (e) {
        logError("Could not get beneficiary (CSRLandingPage)", e);
        setIsError(true);
      }

      const replaceUri = new URI(location.search);
      replaceUri.removeQuery("code");
      history.replace(replaceUri.toString());
    } else if (!beneficiary) {
      setIsError(true);
    } else {
      setLoadingPlan(false);
    }
  };

  useEffect(() => {
    getBeneInfo();
  }, []);

  useEffect(() => {
    if (beneficiary && tealiumLoaded) {
      sendTealiumLink({
        utm_source: "NGD",
        utm_medium: "open_mct",
        utm_campaign: beneficiary.request_id,
      });
    }
  }, [beneficiary, tealiumLoaded]);

  useEffect(() => {
    if (isError) {
      history.replace(`/?mctt=${MctToastType.CSR_BENE_FAIL}`);
    }
  }, [isError]);

  const fetchOECStatus = async (): Promise<void> => {
    try {
      if (year) {
        const status = await getBeneOecStatus(year);

        dispatch({
          type: ActionType.UPDATE_BENEFICIARY_OEC,
          payload: status,
        });
      }
    } catch (e) {
      logError("Failed to get beneficiary OEC status (CSRLandingPage)", e);
    }
  };

  useEffect(() => {
    if (year && beneFetched) {
      fetchOECStatus();
    }
  }, [year, beneFetched]);

  const handleCountySelect = (county?: County, zipcode?: string): void => {
    // istanbul ignore next: tested elsewhere
    sendTealiumEvent("FCSR Options", "Zip code", { event_category: "CSR" });

    if (zipcode)
      dispatch({
        type: ActionType.UPDATE_ZIPCODE,
        payload: zipcode,
      });

    if (county) {
      dispatch({
        type: ActionType.UPDATE_COUNTY,
        payload: county,
      });

      dispatch({
        type: ActionType.UPDATE_FIPS,
        payload: county.fips,
      });
    }
  };

  const handlePlanTypeChoice = (e: SyntheticEvent<HTMLInputElement>): void => {
    const typeChoice = e.currentTarget.value as PlanType;

    // istanbul ignore next: tested elsewhere
    sendTealiumEvent("FCSR Options", TealiumConversions[typeChoice], {
      event_category: "CSR",
    });
    dispatch({
      type: ActionType.UPDATE_PLANTYPE,
      payload: typeChoice,
    });
  };

  const handlePlanYearChoice = (e: SyntheticEvent<HTMLInputElement>): void => {
    const year = e.currentTarget.value;

    dispatch({ type: ActionType.UPDATE_YEAR, payload: year });
  };

  const handleLisStatus = (e: SyntheticEvent<HTMLInputElement>): void => {
    const lis = e.currentTarget.value as LowIncomeSubsidyStatus;
    setLisStatus(lis);

    let action;
    let currentLis;
    let futureLis;
    if (year === mctCurrentPlanYear.toString()) {
      action = ActionType.UPDATE_LIS;
      currentLis = lis;
      futureLis = state.futureLis;
    } else {
      action = ActionType.UPDATE_FUTURE_LIS;
      currentLis = state.lis;
      futureLis = lis;
    }

    dispatch({ type: action, payload: lis });
    fetchCurrentPlan(
      state.beneficiary,
      state.pharmacies,
      currentLis || LowIncomeSubsidyStatus.LIS_NO_HELP,
      dispatch,
      state.nextYearCoverage,
      futureLis || LowIncomeSubsidyStatus.LIS_NO_HELP
    );
  };

  const handleSubmit = (): void => {
    // istanbul ignore next: tested elsewhere
    sendTealiumEvent("FCSR Options", "Continue", { event_category: "CSR" });
    if (state.planType === PlanType.MEDIGAP) {
      history.push(`${Routes.medigap.landingPage}?lang=${language}`);
    } else if (state.prescriptions.length) {
      dispatch({
        type: ActionType.UPDATE_PHARMACY_TYPE,
        payload: PharmacyType.MAIL_AND_RETAIL,
      });

      history.push(
        `${Routes.prescriptionsList}?fips=${
          state.county ? state.county.fips : ""
        }&plan_type=${state.planType}`
      );
    } else {
      history.push(
        `${Routes.drugSearchPreferences}?fips=${
          state.county ? state.county.fips : ""
        }&plan_type=${state.planType}`
      );
    }
  };

  let partADate = "";
  let partBDate = "";

  if (beneficiary) {
    partADate = beneficiary.part_a_start
      ? formatDate(beneficiary.part_a_start, "M/d/yyyy")
      : t("csr_landing.effective_ab_date_missing");

    partBDate = beneficiary.part_b_start
      ? formatDate(beneficiary.part_b_start, "M/d/yyyy")
      : t("csr_landing.effective_ab_date_missing");
  }

  useEffect(() => {
    if (
      state.county &&
      state.planType &&
      (!lisHelpDifferent || (lisHelpDifferent && lisStatus))
    ) {
      setCanContinue(true);
    } else {
      setCanContinue(false);
    }
  }, [state.county, state.planType, lisHelpDifferent, lisStatus]);

  if (!beneficiary) {
    return (
      <AppPage>
        <h1>{t("app.loading")}...</h1>
      </AppPage>
    );
  }

  const showOecSection = beneficiary.oec_status;
  const showFutureCoverage = !!beneficiary.future_coverage.length;
  const showFutureLis = beneficiary.future_lis_level;
  const futureCoverageClass = `infoItem--1-of-3${
    showOecSection ? "" : " infoItem--border-bottom"
  }`;

  return (
    <AppPage heading={t("csr_landing.heading")}>
      <>
        <DegradedIntegrationAlert />
        <div className="beneInfo__container ds-u-margin-bottom--4">
          <h2 className="ds-h2">{t("csr_landing.personal_info")}</h2>
          <div className="beneInfo__content ds-u-padding-bottom--2">
            <InfoItem
              heading={t("csr_landing.bene_name")}
              content={getBeneFirstAndLastName(beneficiary, t)}
            />
            <InfoItem
              heading={t("csr_landing.dob")}
              content={
                beneficiary.birthdate
                  ? formatDate(beneficiary.birthdate, "M/d/yyyy")
                  : t("data_not_available")
              }
            />
            <InfoItem
              heading={t("csr_landing.medicare_number")}
              content={
                beneficiary.mbi
                  ? beneficiary.mbi
                  : t("csr_landing.mbi_not_available")
              }
            />
            <InfoItem
              heading={partADate ? t("csr_landing.part_a_start") : ""}
              content={partADate}
            />
            <InfoItem
              heading={partBDate ? t("csr_landing.part_b_start") : ""}
              content={partBDate}
            />
          </div>
          <hr aria-hidden="true" />
          <h2 className="ds-h2 ds-u-margin-top--2">
            {t("csr_landing.current_enrollment")}
          </h2>
          <div>
            <div className="ds-u-display--flex">
              {beneficiary.current_coverage.length ? (
                <>
                  {loadingPlan ? (
                    <InfoItem
                      className="infoItem--1-of-3 infoItem--border-bottom"
                      heading={""}
                      content={`${t("app.loading")}...`}
                    />
                  ) : (
                    <>
                      {currentCoverage ? (
                        <>
                          <InfoItem
                            className="infoItem--1-of-3 infoItem--border-bottom"
                            heading={t("csr_landing.plan_name")}
                            content={currentCoverage.name}
                          />
                          <InfoItem
                            className="infoItem--1-of-3 infoItem--border-bottom"
                            heading={t("csr_landing.plan_id")}
                            content={makePlanDisplayId(currentCoverage)}
                          />
                          <InfoItem
                            className="infoItem--1-of-3 infoItem--border-bottom"
                            heading={t("csr_landing.plan_type")}
                            content={currentCoverage.plan_type.replace(
                              "PLAN_TYPE_",
                              ""
                            )}
                          />
                        </>
                      ) : (
                        <InfoItem
                          className="infoItem--1-of-3 infoItem--border-bottom"
                          heading={""}
                          content={t("data_not_available")}
                        />
                      )}
                    </>
                  )}
                </>
              ) : (
                <InfoItem
                  className="infoItem--1-of-3 infoItem--border-bottom"
                  heading={""}
                  content={t("none")}
                />
              )}
            </div>
            <div className="ds-u-display--flex">
              <InfoItem
                heading={t("csr_landing.current_subsidy")}
                content={t(
                  year
                    ? getLowIncomeSubsidyStatusLabel(
                        year,
                        beneficiary.lis_level
                      )
                    : ""
                )}
              />
            </div>
          </div>
          {(showOecSection || showFutureCoverage || showFutureLis) && (
            <>
              <hr aria-hidden="true" />
              <h2 className="ds-h2 ds-u-margin-top--2">
                {t("future_enrollment.future")}
              </h2>
              <div>
                {showFutureCoverage && (
                  <div
                    className="ds-u-display--flex"
                    data-testid="future-coverage"
                  >
                    <>
                      <InfoItem
                        className={futureCoverageClass}
                        heading={t("bene_landing.new_coverage")}
                        content={beneficiary.future_coverage[0].name || ""}
                      />
                      <InfoItem
                        className={futureCoverageClass}
                        heading={t("csr_landing.plan_id")}
                        content={makePlanDisplayId(
                          beneficiary.future_coverage[0]
                        )}
                      />
                      <InfoItem
                        className={futureCoverageClass}
                        heading={t("bene_landing.coverage_starts")}
                        content={formatDate(
                          beneficiary.future_coverage[0]
                            .enrollment_effective_date,
                          "M/d/yyyy"
                        )}
                      />
                    </>
                  </div>
                )}
                {showOecSection && (
                  <div className="ds-u-display--flex">
                    <>
                      <InfoItem
                        className="infoItem--1-of-3 infoItem--border-bottom"
                        heading={t("future_enrollment.status")}
                        content={
                          beneficiary?.oec_status?.downloaded
                            ? t("future_enrollment.status.processing")
                            : t("future_enrollment.status.submitted")
                        }
                      />
                      <InfoItem
                        className="infoItem--1-of-3 infoItem--border-bottom"
                        heading={t("future_enrollment.confirmation_number")}
                        content={
                          beneficiary.oec_status
                            ? beneficiary.oec_status.confirmation_number
                            : ""
                        }
                      />
                      <InfoItem
                        className="infoItem--1-of-3 infoItem--border-bottom"
                        heading={""}
                        content={""}
                      />
                    </>
                  </div>
                )}
                {showFutureLis && (
                  <div className="ds-u-display--flex">
                    <InfoItem
                      heading={
                        <>
                          <span>{t("csr_landing.next_subsidy")}</span>{" "}
                          {beneficiary.future_lis_status_start_date ? (
                            <FormattedMessage
                              id="csr_landing.next_subsidy.date"
                              values={{
                                date: formatDate(
                                  beneficiary.future_lis_status_start_date,
                                  "M/d/yyyy"
                                ),
                              }}
                            />
                          ) : (
                            ""
                          )}
                        </>
                      }
                      content={
                        year
                          ? t(
                              getLowIncomeSubsidyStatusLabel(
                                year,
                                beneficiary.future_lis_level
                              )
                            )
                          : ""
                      }
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <div className="">
          <div className="ds-u-border--1 ds-u-padding--2 ds-u-margin-bottom--4">
            <h2 className="ds-h2">{t("csr_landing.bene_plan_search")}</h2>
            <CountyPicker
              label={t("csr_landing.enter_bene_zip")}
              onCountySelect={handleCountySelect}
            />
            {state.county && (
              <p>
                {t("csr_landing.county")}: {state.county.name}
              </p>
            )}
          </div>
          <div className="ds-u-border--1 ds-u-padding--2">
            <h2 className="ds-h2">{t("csr_landing.plan_options")}</h2>
            {!mctYpOeNextYear && !outsideOpenEnrollment && (
              <ChoiceList
                type="radio"
                choices={[
                  {
                    label: mctCurrentPlanYear,
                    value: mctCurrentPlanYear.toString(),
                  },
                  {
                    label: mctCurrentPlanYear + 1,
                    value: (mctCurrentPlanYear + 1).toString(),
                  },
                ]}
                label={t("csr_landing.what_plan_year")}
                name="plan_year_choice"
                onChange={handlePlanYearChoice}
              />
            )}

            <ChoiceList
              type="radio"
              choices={[
                {
                  label: t("landing_page.coverage_selection.mapd.heading"),
                  value: PlanType.MAPD,
                },
                {
                  label: (
                    <FormattedMessage
                      id="landing_page.coverage_selection.pdp.heading"
                      values={{
                        message: <span aria-label="Dee">D</span>,
                      }}
                    />
                  ),
                  value: PlanType.PDP,
                },
                {
                  label: t("landing_page.coverage_selection.medigap.heading"),
                  value: PlanType.MEDIGAP,
                },
              ]}
              label={t("csr_landing.what_bene_plan")}
              className="e2e-plan-option-choice"
              name="plan_option_choice"
              onChange={handlePlanTypeChoice}
            />

            <ChoiceList
              type="checkbox"
              choices={[
                {
                  label: t("csr.lis.different_lis"),
                  value: "different-lis",
                  checked: lisHelpDifferent,
                },
              ]}
              label={t("csr.lis.different_lis_question")}
              className="e2e-lis-is-different-choice"
              name="lis-help-different"
              onChange={(): void => setLisHelpDifferent(!lisHelpDifferent)}
            />

            {lisHelpDifferent && year && (
              <ChoiceList
                type="radio"
                choices={[
                  {
                    label: `${t("csr_landing.lis.deemed_prefix")} ${t(
                      getLowIncomeSubsidyStatusLabel(
                        year,
                        LowIncomeSubsidyStatus.LIS_LEVEL_1A
                      )
                    )}`,
                    value: LowIncomeSubsidyStatus.LIS_LEVEL_1A,
                  },
                  {
                    label: `${t("csr_landing.lis.deemed_prefix")} ${t(
                      getLowIncomeSubsidyStatusLabel(
                        year,
                        LowIncomeSubsidyStatus.LIS_LEVEL_2
                      )
                    )}`,
                    value: LowIncomeSubsidyStatus.LIS_LEVEL_2,
                  },
                  {
                    label: `${t("csr_landing.lis.deemed_prefix")} ${t(
                      getLowIncomeSubsidyStatusLabel(
                        year,
                        LowIncomeSubsidyStatus.LIS_LEVEL_3
                      )
                    )}`,
                    value: LowIncomeSubsidyStatus.LIS_LEVEL_3,
                  },
                  {
                    label: `${t("csr_landing.lis.lis_prefix")} ${t(
                      getLowIncomeSubsidyStatusLabel(
                        year,
                        LowIncomeSubsidyStatus.LIS_LEVEL_1A
                      )
                    )}`,
                    value: LowIncomeSubsidyStatus.LIS_LEVEL_1A,
                  },
                  {
                    label: `${t("csr_landing.lis.lis_prefix")} ${t(
                      getLowIncomeSubsidyStatusLabel(
                        year,
                        LowIncomeSubsidyStatus.LIS_LEVEL_4_100
                      )
                    )}`,
                    value: LowIncomeSubsidyStatus.LIS_LEVEL_4_100,
                  },
                  {
                    label: `${t("csr_landing.lis.lis_prefix")} ${t(
                      getLowIncomeSubsidyStatusLabel(
                        year,
                        LowIncomeSubsidyStatus.LIS_LEVEL_4_100H
                      )
                    )}`,
                    value: LowIncomeSubsidyStatus.LIS_LEVEL_4_100H,
                  },
                  {
                    label: `${t("csr_landing.lis.lis_prefix")} ${t(
                      getLowIncomeSubsidyStatusLabel(
                        year,
                        LowIncomeSubsidyStatus.LIS_LEVEL_4_75
                      )
                    )}`,
                    value: LowIncomeSubsidyStatus.LIS_LEVEL_4_75,
                  },
                  {
                    label: `${t("csr_landing.lis.lis_prefix")} ${t(
                      getLowIncomeSubsidyStatusLabel(
                        year,
                        LowIncomeSubsidyStatus.LIS_LEVEL_4_50
                      )
                    )}`,
                    value: LowIncomeSubsidyStatus.LIS_LEVEL_4_50,
                  },
                  {
                    label: `${t("csr_landing.lis.lis_prefix")} ${t(
                      getLowIncomeSubsidyStatusLabel(
                        year,
                        LowIncomeSubsidyStatus.LIS_LEVEL_4_25
                      )
                    )}`,
                    value: LowIncomeSubsidyStatus.LIS_LEVEL_4_25,
                  },
                  {
                    label: t("csr_landing.lis.no_help"),
                    value: LowIncomeSubsidyStatus.LIS_NO_HELP,
                  },
                ]}
                className="e2e-lis-option-choice"
                label={t("csr.lis.what_lis")}
                name="lis-choice"
                onChange={handleLisStatus}
              />
            )}

            <div className="ds-u-margin-top--2">
              <Button
                onClick={handleSubmit}
                disabled={!canContinue}
                className="e2e-csr-can-continue ds-u-margin-top--1"
                variation="primary"
              >
                {t("csr_landing.continue")}
              </Button>
            </div>
          </div>
        </div>
      </>
    </AppPage>
  );
};

export default CSRLandingPage;
